import React, { useState, useMemo, useEffect } from 'react'
import { Table, DatePicker, Input, Button, Select, Tooltip, Spin } from 'antd'
import styled from '@emotion/styled'
import moment from 'moment'
import { useQuery, useLazyQuery } from '@apollo/client'
import { GET_REF_CLIENTS, GET_REF_GROUPS, GET_REF_EMPLEADOS } from 'Queries'

const dateFormat = 'YYYY/MM/DD'

export const FamiliesClientList = ({
  codDistribuidor,
  codObjetivo,
  year,
  fechaDesde,
  fechaHasta,
  codigoEmpleado,
  nivelEmpleado,
  isMensual
}) => {
  const objectiveStart = moment(fechaDesde, 'x').utc().startOf('day')
  const objectiveEnd = moment(fechaHasta, 'x').utc().endOf('day')
  // Ajuste: si hoy cae dentro del objetivo, filtrar desde día 1 de este mes hasta hoy
  const today = moment().utc()
  const todayInObjective = isMensual? today.isSameOrAfter(objectiveStart) && today.isSameOrBefore(objectiveEnd) : false
  
  const effectiveStart = todayInObjective ? today.clone().startOf('month') : objectiveStart
  const effectiveEnd = todayInObjective ? today : objectiveEnd
  
  // Estados iniciales
  const initialPeriod = [effectiveStart, effectiveEnd]
  const initialSearchTerm = ''
  const initialNivel = undefined
  const initialEmpleado = undefined
  const initialNumReferencias = undefined
  
  const [period, setPeriod] = useState(initialPeriod)
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm)
  const [selectedNivel, setSelectedNivel] = useState(initialNivel)
  const [selectedEmpleado, setSelectedEmpleado] = useState(initialEmpleado)
  const [selectedNumReferencias, setSelectedNumReferencias] = useState(initialNumReferencias)
  const [empleadosFiltrados, setEmpleadosFiltrados] = useState([])

  // Valores para la paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]= useState(100);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  

  // Carga de los datos de la tabla
  const {data, loading, error} = useQuery(GET_REF_CLIENTS, {
    variables: {
      codDistribuidor: codDistribuidor,
      codObjetivo: codObjetivo,
      anio: year,
      codEmpleado: selectedEmpleado || codigoEmpleado,
      nivEmpleado: (selectedEmpleado && selectedNivel)? selectedNivel : nivelEmpleado,
      page: currentPage - 1,
      size: pageSize,
      searchTerm: searchTerm || undefined,
      sortBy: sortBy || undefined,
      sortDir: sortDir || undefined,
      // status: undefined,
      countGroup: selectedNumReferencias ? parseInt(selectedNumReferencias.slice(3)) : undefined,
      fromDate: period[0]?.format('YYYY-MM-DD') || undefined,
      toDate: period[1]?.format('YYYY-MM-DD') || undefined,
    }
  })


  // Carga de las segmentaciones
  const { data: refGroupsData, loading: loadingRefGroups } = useQuery(GET_REF_GROUPS, {
    variables: {
      codDistribuidor: codDistribuidor,
      codObjetivo: codObjetivo,
      anio: year,
      withExceptions: false
    }
  })

  // Carga de empleados con sus roles/niveles
  const { data: empleadosData, loading: loadingEmpleados, error: errorEmpleados} = useQuery(GET_REF_EMPLEADOS, {
    skip: !codDistribuidor || !year || !codObjetivo,
    variables: {
      codDistribuidor: codDistribuidor,
      anio: year,
      codObjetivo: codObjetivo
    },
  })

  // Derivar niveles únicos desde datos reales
  const nivelesDisponibles = useMemo(() => {
    const empleados = empleadosData?.refEmpleados || []
    const map = new Map()
    empleados.forEach(emp => {
      if (emp.nivelEmpleado && !map.has(emp.nivelEmpleado.id)) {
        map.set(emp.nivelEmpleado.id, emp.nivelEmpleado)
      }
    })
    return Array.from(map.values())
  }, [empleadosData])

  // Objeto con todos los filtros activos (reutilizable)
  const activeFilters = {
    codDistribuidor: codDistribuidor,
      codObjetivo: codObjetivo,
      anio: year,
      codEmpleado: selectedEmpleado || codigoEmpleado,
      nivEmpleado: (selectedEmpleado && selectedNivel)? selectedNivel : nivelEmpleado,
      // page: 0,
      // size: pageSize,
      searchTerm: searchTerm || undefined,
      sortBy: sortBy || undefined,
      sortDir: sortDir || undefined,
      // status: undefined,
      countGroup: selectedNumReferencias ? parseInt(selectedNumReferencias.slice(3)) : undefined,
      fromDate: period[0]?.format('YYYY-MM-DD') || undefined,
      toDate: period[1]?.format('YYYY-MM-DD') || undefined,
  }

  // useLazyQuery para la exportación de todos los datos
  const [getRefClientsForExport] = useLazyQuery(GET_REF_CLIENTS, {
    fetchPolicy: 'network-only'
  })

  // Generar columnas dinámicas desde GET_REF_GROUPS
  const segmentacionColumns = useMemo(() => {
    let groups = refGroupsData?.refGroups || []
    // Si refGroups es un string (JSON stringificado), parsearlo
    if (typeof groups === 'string') {
      try {
        groups = JSON.parse(groups)
      } catch (e) {
        console.error('Error parsing refGroups:', e)
        groups = []
      }
    }
    // Asegurar que es array
    if (!Array.isArray(groups)) {
      groups = []
    }
    return groups.map((seg, idx) => ({
      key: seg.groupName || seg,
      title: seg.groupName || seg
    }))
  }, [refGroupsData?.refGroups])

  // Mapear datos del backend
  const mappedData = useMemo(() => {
    return (data?.refClients?.clientes || []).map(item => ({
      key: item.codPuntoVenta,
      codPdv: item.codPuntoVenta,
      nombrePdv: item.pdvName,
      refPeriodoActual: item.famPerActual,
      refAA: item.famAA,
      segmentaciones: segmentacionColumns.reduce((acc, seg) => ({
        ...acc,
        [seg.key]: item.segIds?.includes(seg.key)
      }), {})
    }))
  }, [data?.refClients?.clientes, segmentacionColumns])

  // Filtrar empleados según el nivel seleccionado
  useEffect(() => {
    if (selectedNivel) {
      const empleados = empleadosData?.refEmpleados || []
      const employees = empleados.filter(emp => emp.nivelEmpleado?.id === selectedNivel)
      setEmpleadosFiltrados(employees)
    } else {
      setEmpleadosFiltrados([])
    }
  }, [selectedNivel, empleadosData])

  // Reset de página al cambiar filtros: gestionado inline en cada handler

  // Función para resetear todos los filtros
  const handleResetFilters = () => {
    setCurrentPage(1)
    setPageSize(100)
    setPeriod(initialPeriod)
    setSearchTerm(initialSearchTerm)
    setSelectedNivel(initialNivel)
    setSelectedEmpleado(initialEmpleado)
    setSelectedNumReferencias(initialNumReferencias)
  }

  // Calcular el número total de segmentaciones distintas desde GET_REF_GROUPS
  const totalSegmentaciones = useMemo(() => {
    return refGroupsData?.refGroups?.length || 0
  }, [refGroupsData?.refGroups])

  // Verificar si hay filtros activos
  const hasActiveFilters = searchTerm || selectedNivel || selectedEmpleado || selectedNumReferencias || 
    (period && (period[0]?.valueOf() !== initialPeriod[0]?.valueOf() || period[1]?.valueOf() !== initialPeriod[1]?.valueOf()))
    || pageSize !== 100

  // Filtrar datos por búsqueda y número de familias
  const filteredData = useMemo(() => {
    let data = mappedData
    
    // Filtro por número de familias (solo si hay un valor seleccionado y no es undefined)
    if (selectedNumReferencias !== undefined && selectedNumReferencias !== null) {
      const numFamilias = selectedNumReferencias === 'ref0' ? 0 : parseInt(selectedNumReferencias.replace('ref', ''))
      data = data.filter(item => {
        // Contar cuántas familias (segmentaciones) tiene activas este cliente
        const familiasActivas = Object.values(item.segmentaciones || {}).filter(Boolean).length
        return familiasActivas === numFamilias
      })
    }
    
    return data
  }, [mappedData, selectedNumReferencias])

  // Columnas fijas
  const fixedColumns = [
    {
      title: 'Código PdV',
      dataIndex: 'codPdv',
      key: 'codPdv',
      width: 120,
      fixed: 'left',
      align: 'center',
      sorter: (a, b) => {
        if (!a || !b) return 0;
        return String(a.codPdv || '').localeCompare(String(b.codPdv || ''));
        // return (Number(a.codPdv) || 0) - (Number(b.codPdv) || 0);
      },
      render: (value) => {
        const obj = {
          children: value,
          props: { style: { background: '#D4EFDF' } },
        }
        return obj
      },
    },
    {
      title: 'Nombre PdV',
      dataIndex: 'nombrePdv',
      key: 'nombrePdv',
      width: 250,
      fixed: 'left',
      sorter: (a, b) => {
        if (!a || !b) return 0;
        return String(a.nombrePdv || '').localeCompare(String(b.nombrePdv || ''));
      },
      ellipsis: true,
      render: (value) => {
        const obj = {
          children: value,
          props: { style: { background: '#D4EFDF' } },
        }
        return obj
      },
    },
    {
      title: 'Fam Periodo Actual',
      dataIndex: 'refPeriodoActual',
      key: 'refPeriodoActual',
      width: 150,
      align: 'center',
      sorter: (a, b) => {
        if (!a || !b) return 0;
        return (a.refPeriodoActual || 0) - (b.refPeriodoActual || 0);
      },
      defaultSortOrder: 'descend',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Fam AA (misma fecha)',
      dataIndex: 'refAA',
      key: 'refAA',
      width: 160,
      align: 'center',
      sorter: (a, b) => {
        if (!a || !b) return 0;
        return (a.refAA || 0) - (b.refAA || 0);
      },
    },
  ]

  // Columnas dinámicas de segmentación (ya creado arriba, aquí solo usamos)
  const segmentacionColumnsTable = segmentacionColumns.map(seg => ({
    title: <Tooltip title={seg.title}><span style={{maxWidth: '70px', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}}>{seg.title.length > 10 ? seg.title.substring(0, 10) + '...' : seg.title}</span></Tooltip>,
    dataIndex: ['segmentaciones', seg.key],
    width: 80,
    key: seg.key,
    // width: 100,
    align: 'center',
    // sorter: (a, b) => {
    //   if (a === undefined || a === null || b === undefined || b === null) return 0;
    //   return (a ? 1 : 0) - (b ? 1 : 0);
    // },
    render: (value) => value ? <CheckMark>X</CheckMark> : '',
  }))

  // Mapeo de campos para ordenamiento (dataIndex -> nombre backend)
  const fieldMapping = {
    'codPdv': 'COD_PTO_VENTA',
    'nombrePdv': 'NOMBRE',
    'refPeriodoActual': 'FAM_PERIODO',
    'refAA': 'FAM_PERIODO_AA'
  }

  // Combinar todas las columnas
  const columns = [...fixedColumns, ...segmentacionColumnsTable]

  // Handler para cambios en la tabla (ordenamiento y paginación)
  const handleTableChange = (pagination, filters, sorter) => {
    const newPageSize = pagination.pageSize
    const pageSizeChanged = newPageSize !== pageSize
    setCurrentPage(pageSizeChanged ? 1 : pagination.current)
    setPageSize(newPageSize)
    
    if (sorter.field && sorter.order) {
      const backendFieldName = fieldMapping[sorter.field] || sorter.field
      setSortBy(backendFieldName)
      setSortDir(sorter.order === 'ascend' ? 'ASC' : 'DESC')
    } else {
      setSortBy(null)
      setSortDir(null)
    }
  }

  // Función de exportación a CSV descargando TODOS los datos
  const handleExportAll = async () => {
    setIsExportingCSV(true)
    try {
      // Obtener la primera página para saber el total
      const firstPageResult = await getRefClientsForExport({
        variables: {
          ...activeFilters,
          page: 0,
          size: 200, // Usar size más grande para menos iteraciones
        }
      })

      const totalClientes = firstPageResult.data?.refClients?.totalClientes || 0
      const pageSize = 200
      const totalPages = Math.ceil(totalClientes / pageSize)

      // Array para acumular todos los datos
      let allData = firstPageResult.data?.refClients?.clientes || []

      // Iterar desde página 1 hasta la última (ya tenemos página 0)
      for (let page = 1; page < totalPages; page++) {
        const result = await getRefClientsForExport({
          variables: {
            ...activeFilters,
            page: page,
            size: pageSize,
          }
        })
        allData = [...allData, ...(result.data?.refClients?.clientes || [])]
      }

      // Mapear y preparar datos para CSV
      const mappedExportData = allData.map(item => ({
        key: item.codPuntoVenta,
        codPdv: item.codPuntoVenta,
        nombrePdv: item.pdvName,
        refPeriodoActual: item.famPerActual,
        refAA: item.famAA,
        segmentaciones: segmentacionColumns.reduce((acc, seg) => ({
          ...acc,
          [seg.key]: item.segIds?.includes(seg.key)
        }), {})
      }))

      // Generar CSV
      const headers = [
        'Código PdV',
        'Nombre PdV',
        'Fam Periodo Actual',
        'Fam AA',
        ...segmentacionColumns.map(s => s.title)
      ]

      const csvContent = [
        headers.join(';'),
        ...mappedExportData.map(row => [
          row.codPdv,
          `"${row.nombrePdv}"`,
          row.refPeriodoActual,
          row.refAA,
          ...segmentacionColumns.map(s => 
            row.segmentaciones[s.key] ? 'X' : ''
          )
        ].join(';'))
      ].join('\n')

      // Descargar archivo
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `familias_lista_clientes_${moment().format('YYYYMMDD')}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error al descargar CSV:', error)
    } finally {
      setIsExportingCSV(false)
    }
  }

  return (
    <Spin 
      spinning={isExportingCSV || loadingEmpleados || loadingRefGroups} 
      tip={loadingEmpleados || loadingRefGroups ? "Cargando estructura..." : isExportingCSV ? "Descargando..." : "" }
      size="large"
    >
      <Container>
      <ControlsWrapper>
        <LeftControls>
          <Button
            type="primary"
            onClick={handleExportAll}
            loading={isExportingCSV}
            disabled={isExportingCSV}
            size="large"
          >
            {isExportingCSV ? 'Descargando...' : 'Descargar CSV'}
          </Button>
        </LeftControls>
        <RightControls>
          <Input
            mode="default"
            size="small"
            placeholder="Cliente"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }}
            style={{ width: 160, minWidth: 120 }}
          />
          <Select
            mode="default"
            size="small"
            placeholder="Rol"
            value={nivelesDisponibles.length ? selectedNivel : null}
            onChange={(nivel) => {
              setSelectedNivel(nivel)
              setSelectedEmpleado(undefined)
              setCurrentPage(1)
            }}
            style={{ width: 180, minWidth: 120 }}
            dropdownStyle={{ zIndex: 999 }}
            showArrow={true}
          >
            {nivelesDisponibles.map(({ id, desc }) => (
              <Select.Option key={id} value={id}>
                {desc}
              </Select.Option>
            ))}
          </Select>
          <Select
            disabled={!selectedNivel}
            mode="default"
            size="small"
            placeholder="Empleado"
            value={empleadosFiltrados.length ? selectedEmpleado : null}
            onChange={(empleado) => { setSelectedEmpleado(empleado); setCurrentPage(1) }}
            style={{ width: 200, minWidth: 120 }}
            dropdownStyle={{ zIndex: 999 }}
            showArrow={true}
          >
            {empleadosFiltrados.map(({ codEmpleado, nombreEmpleado }) => (
              <Select.Option key={codEmpleado} value={codEmpleado}>
                {nombreEmpleado}
              </Select.Option>
            ))}
          </Select>
          <Select
            mode="default"
            size="small"
            placeholder="Nº familias"
            value={selectedNumReferencias}
            onChange={(numRefs) => { setSelectedNumReferencias(numRefs); setCurrentPage(1) }}
            style={{ width: 140 }}
            dropdownStyle={{ zIndex: 999 }}
            showArrow={true}
          >
            <Select.Option key={-1} value={null}>
              ...
            </Select.Option>
            {Array.from({ length: totalSegmentaciones + 1 }, (_, index) => (
              <Select.Option key={`ref${index}`} value={`ref${index}`}>
                {index} familia{index !== 1 ? 's' : ''}
              </Select.Option>
            ))}
          </Select>
          <DatePicker.RangePicker
            allowClear={false}
            disabledDate={current => {
              return current && (current < objectiveStart || objectiveEnd < current)
            }}
            onChange={selection => {
              if (selection && selection.length > 1) {
                const [start, end] = selection
                setPeriod(start <= end ? selection : [end, start])
                setCurrentPage(1)
              }
            }}
            size="small"
            value={period}
            format={dateFormat}
            style={{ width: '240px' }}
          />
          <Button
            disabled={!hasActiveFilters}
            type="link"
            size="large"
            onClick={handleResetFilters}
          >
            Resetear filtros
          </Button>
        </RightControls>
      </ControlsWrapper>

      <TableWrapper>
        <StyledTable
          dataSource={filteredData}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          pagination={{
            pageSize: pageSize,
            current: currentPage,
            total: data?.refClients?.totalClientes || 0,
            showSizeChanger: true,
            pageSizeOptions: ['50', '100', '200'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
          }}
          scroll={{ y: 'calc(100vh - 435px)', x: Math.max(1200, segmentacionColumns.length * 100 + 600) }}
          locale={{ emptyText: 'No hay puntos de venta para la búsqueda' }}
        />
      </TableWrapper>
      </Container>
    </Spin>
  )
}

const Container = styled.div`
  padding: 0;
`

const ControlsWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-width: 100%;
  gap: 10px;
`

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const RightControls = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  > * {
    margin: 0 5px;
  }
`

const TableWrapper = styled.div`
  background: transparent;
  box-shadow: none;
  margin-bottom: 5px;
  
  & .ant-table {
    background: white;
    box-shadow: 0 4px 12px 0 rgba(200, 198, 198, 0.5);
  }
`

const StyledTable = styled(Table)`
  & .ant-table-thead > tr > th {
    font-size: 14px;
    font-weight: 700;
    background: #FFFFFF !important;
    color: #205527;
    padding: 8px 8px !important;
  }
  & .ant-table-tbody > tr > td {
    background: #F3FAF3;
    border-top: 1px solid darkGray;
    border-bottom: none;
  }
  & .ant-pagination {
    background: transparent !important;
    padding: 12px 0 !important;
    font-size: 12px;
  }
  & .ant-pagination-item,
  & .ant-pagination-prev,
  & .ant-pagination-next,
  & .ant-pagination-options {
    font-size: 12px;
  }
`

const CheckMark = styled.span`
  color: #52c41a;
  font-weight: bold;
  font-size: 16px;
`
